import { API } from "@swivl-mono/api-helper";
import { LocationVarInheritance, LocationVarKey } from "@swivl-mono/types";
import { HelperText, Input, Label, Select } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { TbExternalLink } from "react-icons/tb";

import { useLocationVariables, useMergedLocationVariables } from "../../../../Hooks/useLocationVariables";
import FMSModel from "../../../../Models/FMS/FMS.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import { AlertsModel } from "../../../../Models/Notifications/Alerts.Model";
import LocationVarsModel from "../../../../Models/Variables/LocationVars.Model";
import { StepOutcome, WizardFooter, WizardStepShell } from "./WizardChrome";

interface FmsStepProps {
  locationId: number;
  groupId: number;
  fmsId: string;
  onFmsId: (value: string) => void;
  integrationId: string | null;
  onIntegrationId: (value: string | null) => void;
  onBack?: () => void;
  onClose: () => void;
  onNext: (outcome: StepOutcome) => void;
}

// FMS sync fields: which integration drives the sync, and the location's external FMS ID.
export function FmsStep({
  locationId,
  groupId,
  fmsId,
  onFmsId,
  integrationId,
  onIntegrationId,
  onBack,
  onClose,
  onNext
}: FmsStepProps): React.ReactElement {
  const { FMSs } = FMSModel.useState();
  const [saving, setSaving] = useState(false);

  // Inherited from group/org — groupId is required or group-level inheritance won't resolve.
  const vars = useLocationVariables<LocationVarKey.CortexFacilityFMSIntegrationId>(
    [LocationVarKey.CortexFacilityFMSIntegrationId],
    groupId,
    locationId
  );
  const inheritedIntegrationId = vars?.[LocationVarKey.CortexFacilityFMSIntegrationId];
  useEffect(() => {
    if (integrationId === null && inheritedIntegrationId) onIntegrationId(inheritedIntegrationId);
  }, [inheritedIntegrationId]);

  // FMS ID is unique per location — only prefill when set here, never inherited.
  const mergedFmsId = useMergedLocationVariables<LocationVarKey.CortexFacilityFMSId>([LocationVarKey.CortexFacilityFMSId], groupId, locationId);
  const ownFmsId = mergedFmsId?.[LocationVarKey.CortexFacilityFMSId];
  useEffect(() => {
    if (!fmsId && ownFmsId?.inheritance === LocationVarInheritance.NONE && ownFmsId.value) onFmsId(ownFmsId.value);
  }, [ownFmsId]);

  // Reload on mount and when the tab regains focus, so a just-added integration appears.
  useEffect(() => {
    const reload = (): void => void FMSModel.load(true);
    reload();
    window.addEventListener("focus", reload);
    return () => window.removeEventListener("focus", reload);
  }, []);

  // Persists whatever's currently entered, independent of advancing/closing.
  const persist = async (): Promise<void> => {
    const patches: { key: LocationVarKey; value: string }[] = [];
    if (integrationId) patches.push({ key: LocationVarKey.CortexFacilityFMSIntegrationId, value: integrationId });
    if (fmsId.trim()) patches.push({ key: LocationVarKey.CortexFacilityFMSId, value: fmsId.trim() });
    // ENG-3388 D-6: both ids entered turns sync on for the new location.
    if (integrationId && fmsId.trim()) patches.push({ key: LocationVarKey.CortexFacilityFMSSyncEnabled, value: "1" });
    if (patches.length === 0) return;
    setSaving(true);
    try {
      await API.Cortex.saveRaw({ orgId: Member.orgId, locationId, patches });
      await LocationVarsModel.loadAllVariables(true);
    } catch (err) {
      // Surface the failure instead of letting it advance/close silently.
      AlertsModel.errorToast(`Couldn't save FMS details: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (): Promise<void> => {
    await persist();
    // Both fields must be filled to count as completed; otherwise it's a skip.
    onNext(integrationId && fmsId.trim() ? "completed" : "skipped");
  };

  // "Save & close" must actually save the draft, not just close over it.
  const handleSaveAndClose = async (): Promise<void> => {
    await persist();
    onClose();
  };

  return (
    <WizardStepShell
      footer={
        <WizardFooter
          onBack={onBack}
          onClose={() => void handleSaveAndClose()}
          onSkip={() => onNext("skipped")}
          onApprove={() => void handleApprove()}
          saving={saving}
        />
      }
    >
      <div>
        <Select
          label="FMS Integration"
          options={(FMSs ?? []).map((fms) => ({ label: `${fms.name} (${fms.type})`, value: String(fms.id) }))}
          selectedValues={integrationId ? [integrationId] : []}
          onChange={(values) => onIntegrationId(values[0] ?? null)}
        />
        <a
          href="/settings/fms-integrations"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-xs font-normal text-grey-500 hover:text-black"
        >
          Manage integrations <TbExternalLink className="size-3" />
        </a>
      </div>
      <div>
        {/* Input's own helperText renders below the box, not above it, so composed manually here. */}
        <Label className="mb-1 block">FMS ID</Label>
        <HelperText className="mb-1">The location's ID in the FMS (Sitelink, Cubby, etc.) - syncs units, tenants, leases, and leads.</HelperText>
        <Input value={fmsId} onChange={(e) => onFmsId(e.target.value)} />
        <a
          href="https://learn.tryswivl.com/fms-integrations#connecting-your-fms"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-xs font-normal text-grey-500 hover:text-black"
        >
          How to find your FMS ID <TbExternalLink className="size-3" />
        </a>
      </div>
    </WizardStepShell>
  );
}
